import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ButtonPlus } from './Buttons/ButtonPlus';
import { SidebarContent } from './Sidebar/Sidebar';
import { MiniMenuIcons } from './Sidebar/IkonsSidebar';
import { ButtonMinus } from './Buttons/ButtonMinus';
import { AuthModal } from './modals/AuthModal';
import UserAvatarButton from './Buttons/UserAvatarButton';
import { useQuery } from '@apollo/client';
import { Footer } from './Footer';
import { ME } from '../graphql/queries';
import { PrimaryButton } from '@fluentui/react';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { CookieConsentBanner } from './Cookie';
import { TOCSidebar } from './TOC';

// === Стили ===

const LayoutContainer = styled.div<{ ismobile: boolean }>`
  position: relative;
  width: 100%;
  height: 100dvh;
   overflow: ${({ ismobile }) => (ismobile ? 'auto' : 'hidden')};
  display: ${({ ismobile }) => (ismobile ? 'block' : 'flex')};
`;

const RightButtonContainer = styled.div`
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 1000;
  display: flex;
  align-items: center;
`;

const MiniButtonsContainer = styled.div`
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%); /* 🔥 Центрируем */
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  z-index: 5;
`;

const Sidebar = styled.aside<{ isopen: boolean; ismobile: boolean; istocopen: boolean }>`
  width: ${({ ismobile, isopen }) =>
    ismobile
      ? (isopen ? '290px' : '45px')  /* 🔥 Не 0, а 45px в закрытом состоянии */
      : isopen
        ? '290px'
        : '45px'
  };
  background-color: #0078d4;
  transition: width 0.3s ease;
  position: ${({ ismobile }) => (ismobile ? 'fixed' : 'relative')};
  top: 0;
  left: 0;
  bottom: 0;
  height: 100dvh;
  z-index: 1100;
  flex-shrink: 0;
  overflow: hidden;
  margin-right: ${({ ismobile, istocopen }) => (ismobile ? '0' : istocopen ? '0' : '30px')};
  box-shadow: ${({ ismobile }) =>
    ismobile
      ? '2px 0 8px rgba(0, 0, 0, 0.2)'
      : '1px 0 4px rgba(248, 246, 246, 0.1)'
  };
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  top: 16px;
  right: 15px;
  z-index: 10;
`;

const Overlay = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
  transition: opacity 0.3s ease;
  z-index: 1050;
  pointer-events: ${({ visible }) => (visible ? 'auto' : 'none')};
`;

// === MainContent — ТОЧНО КАК В ОРИГИНАЛЕ ===
const MainContent = styled.main<{ ismobile: boolean }>`
  flex: 1;
  padding: 24px;
  transition: margin-left 0.3s ease;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  margin-left: ${({ ismobile }) => (ismobile ? '45px' : 0)};
  min-width: 0;
  min-height: 80dvh;
`;

// === FlexContainer — ТОЧНО КАК В ОРИГИНАЛЕ ===
const FlexContainer = styled.div`
  flex: 1 0 auto;
  min-width: 0;
`;

// === Интерфейс ===
interface LayoutProps {
  children?: React.ReactNode;
}


// === Основной компонент ===
const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isAuthModalOpen, setAuthModalOpen] = useState(false);
  const [isTOCOpen, setTOCOpen] = useState(false); 

  const [cookieAccepted, setCookieAccepted] = useState(() => {
  return localStorage.getItem('cookieConsent') === 'true';
  });

  const handleCookieAccept = () => {
    localStorage.setItem('cookieConsent', 'true');
    setCookieAccepted(true);
  };


  const { data } = useQuery(ME);
  const isAuthenticated = !!data?.me;

  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    if (isMobile && isSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobile, isSidebarOpen]);

    // ✅ Слушаем событие открытия оглавления
    useEffect(() => {
      const toggleTOC = () => setTOCOpen(prev => !prev);
      const closeTOC = () => setTOCOpen(false);
      window.addEventListener('toggleTOC', toggleTOC);
      window.addEventListener('closeTOC', closeTOC);
      return () => {
        window.removeEventListener('toggleTOC', toggleTOC);
        window.removeEventListener('closeTOC', closeTOC);
      };
    }, []);

  return (
    <LayoutContainer ismobile={isMobile}>

      {/* Сайдбар */}
      <Sidebar isopen={isSidebarOpen} ismobile={isMobile} istocopen={isTOCOpen}>
        {/* === На мобильных: всегда показываем иконку открытия ВНУТРИ сайдбара (45px) === */}
        {isMobile && !isSidebarOpen && (
          <MiniButtonsContainer>
            <ButtonPlus
              onClick={() => setSidebarOpen(true)}
              aria-label="Открыть меню"
            />
            <MiniMenuIcons /> 
          </MiniButtonsContainer>
        )}

        {!cookieAccepted && <CookieConsentBanner onAccept={handleCookieAccept} />}

        {/* === На мобильных: полное меню при открытом состоянии === */}
        {isMobile && isSidebarOpen && (
          <>
            <CloseButtonWrapper>
              <ButtonMinus
                onClick={() => setSidebarOpen(false)}
                aria-label="Закрыть меню"
              />
            </CloseButtonWrapper>
            <div style={{ padding: '18px' }}>
              <SidebarContent istocopen={isTOCOpen} />
            </div>
          </>
        )}

        {/* === Десктоп: мини-иконки при закрытом состоянии === */}
        {!isMobile && !isSidebarOpen && (
          <MiniButtonsContainer>
            <ButtonPlus
              onClick={() => setSidebarOpen(true)}
              aria-label="Открыть меню"
            />
            <MiniMenuIcons />
          </MiniButtonsContainer>
        )}

        {/* === Десктоп: полное меню при открытом состоянии === */}
        {!isMobile && isSidebarOpen && (
          <>
            <CloseButtonWrapper>
              <ButtonMinus
                onClick={() => setSidebarOpen(false)}
                aria-label="Закрыть меню"
              />
            </CloseButtonWrapper>
            <div style={{ padding: '18px' }}>
              <SidebarContent istocopen={isTOCOpen} />
            </div>
          </>
        )}
      </Sidebar>

      <TOCSidebar
        isOpen={isTOCOpen}
        isMobile={isMobile}
        sidebarOpen={isSidebarOpen}
        onClose={() => setTOCOpen(false)}
      />


      {/* Overlay */}
      {isMobile && isSidebarOpen && (
        <Overlay
          visible={isSidebarOpen}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Основной контент — ТОЧНО КАК В ОРИГИНАЛЕ */}
      <MainContent ismobile={isMobile}>
        <FlexContainer>{children}</FlexContainer>
        <Footer />
      </MainContent>

      {/* Кнопка авторизации */}
      <RightButtonContainer>
        {isAuthenticated ? (
          <UserAvatarButton />
        ) : (
          <PrimaryButton
            text="Войти"
            onClick={() => setAuthModalOpen(true)}
          />
        )}
      </RightButtonContainer>

      <AuthModal
        isOpen={isAuthModalOpen}
        onDismiss={() => setAuthModalOpen(false)}
      />
    </LayoutContainer>
  );
};

export default Layout;